import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import axios from "axios";

import { ORANGE_BAR_PREVIEW_URL } from "./constants";
import { getHeaders } from "./utils";
import "./orange-bar-modal.css";

/**
 * Shows the TradingView layout the orange bar alerts are read from.
 *
 * The alerts never look at pixels -- they ask the chart model what colour it
 * painted each bar -- so there is nothing to configure here and nothing to save.
 * This exists for the one question the JSON debug endpoint cannot answer: is the
 * page actually up, on the right symbols, with the study painting what the sweep
 * says it is seeing.
 *
 * Renders on demand rather than on open: a cold render launches Chromium, and
 * opening the dropdown by accident should not cost a minute of CPU.
 */
export default function OrangeBarModal({ show, onHide }) {
  const [preview, setPreview] = useState(null);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    if (show) {
      setPreview(null);
    }
  }, [show]);

  const renderPreview = async () => {
    setRendering(true);
    try {
      // With no session open this opens one, which is a page load plus the wait
      // for the studies to compute -- the better part of a minute on a cold box.
      const response = await axios({
        method: "get",
        url: ORANGE_BAR_PREVIEW_URL,
        headers: getHeaders(),
        withCredentials: true,
        timeout: 180000,
      });
      setPreview(response.data);
    } catch (error) {
      alert("Failed to screenshot the signal layout.\n\n" + describe(error));
    } finally {
      setRendering(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered data-bs-theme="dark">
      <Modal.Header closeButton className="bg-dark text-light border-secondary">
        <Modal.Title>Orange Bar Layout</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-light orange-bar-body">
        <div className="orange-bar-toolbar">
          <Button size="sm" variant="primary" onClick={renderPreview} disabled={rendering}>
            {rendering ? <Spinner as="span" size="sm" /> : "Preview"}
          </Button>
          {preview && (
            <span className="orange-bar-status">
              {/* Whether this is the page the scheduler is watching, or a
                  throwaway one opened for this request and already closed. */}
              {preview.live ? "live session" : "throwaway page"} · {preview.capturedAt} ET ·{" "}
              {preview.viewport.width}×{preview.viewport.height}
            </span>
          )}
        </div>

        {preview ? (
          <div className="orange-bar-image">
            <img src={preview.image} alt="Signal layout" />
          </div>
        ) : (
          <div className="orange-bar-empty">
            {rendering
              ? "Rendering… a cold session has to load the layout and wait for the studies."
              : "Press Preview to screenshot the signal layout the orange bar alerts read."}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function describe(error) {
  const details = error.response && error.response.data;
  if (details && details.error) {
    return details.error;
  }
  return error.message || "No error details provided.";
}
